import { ChangeDetectionStrategy, Component, computed, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControlStatus, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs/operators';
import { FormStatus, ShiftLogTaskFormValue } from '../task.dialog.model';
import { ShiftLog } from '../../../../models/shift-log.model';
import { TaskFormDialogBase } from '../task.form-base.dialog';
import { AppDropdownComponent, AppDropdownOption } from '../../../../shared/components/app-dropdown/app-dropdown.component';
import { ACTIVITY_TYPES_SHIFT_DATA } from '../activity-types.data';
import { NO_PROGRAM_LABEL } from '../../../../shared/constants/program';

@Component({
  standalone: true,
  selector: 'dr-ops-stream-ui-shiftlog-task-dialog',
  imports: [CommonModule, ReactiveFormsModule, AppDropdownComponent],
  templateUrl: './shiftlog-task.dialog.html',
  styleUrls: ['./../task.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftLogTaskDialog extends TaskFormDialogBase<ShiftLog, ShiftLogTaskFormValue> {
  readonly activityTypes = signal<string[]>(ACTIVITY_TYPES_SHIFT_DATA);

  readonly activityOptions = computed<AppDropdownOption[]>(() =>
    this.activityTypes().map((value, index) => ({
      value: value,
      label: value,
    }))
  );

  readonly programOptionsWithOther = computed<AppDropdownOption[]>(() => [
    ...this.programOptions(),
    { value: NO_PROGRAM_LABEL, label: 'Other' },
  ]);

  readonly utcOnlyOptions: AppDropdownOption[] = [{ value: 'UTC', label: 'UTC' }];

  // -----------------------
  // Form
  // -----------------------
  readonly form = this.fb.group({
    programId: this.fb.control<string>(NO_PROGRAM_LABEL, { nonNullable: true }),
    notes: this.fb.control<string>('', { nonNullable: true }),
    dueDate: this.fb.control<string | null>(null),
    timezone: this.fb.control<string>('UTC', { nonNullable: true }),
    activity: this.fb.control<string>('', { nonNullable: true, validators: [Validators.required] }),
  });

  readonly formValid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map((s: FormControlStatus) => s === FormStatus.VALID)
    ),
    { initialValue: this.form.valid }
  );

  readonly canSubmit = computed(() => !this.isViewOnly() && this.formValid());
  readonly confirmLabel = computed(() => (this.isEdit() ? 'Save' : 'Confirm'));

  constructor() {
    super();
    /**
     * Initialize the form from data
     */
    effect(() => {
      const defaults = this.data.defaults ?? {};

      const initial: ShiftLogTaskFormValue = {
        programId: NO_PROGRAM_LABEL,
        notes: '',
        dueDate: '',
        activity: '',
        ...defaults,
      };

      this.form.reset(initial);

      if (this.isViewOnly()) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }

      this.form.controls.timezone.setValue('UTC', { emitEvent: false });
      this.form.controls.timezone.disable({ emitEvent: false });
    });
  }
}
